import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { AppContext } from "../../../context/app";
import {
  BulkCheckinMutationVariables,
  CancelCheckInMutationVariables,
  CancelReservationMutationVariables,
  CheckInMutationVariables,
  RemoveFromWaitListMutationVariables,
  RemoveLateCancelMutationVariables,
  useBulkCheckinMutation,
  useCancelCheckInMutation,
  useCancelReservationMutation,
  useCheckInMutation,
  useRemoveFromWaitListMutation,
  useRemoveLateCancelMutation,
} from "./CalendarProvider-generated.hooks";

interface CalendarProviderProps {
  children?: ReactNode;
}

const useCalendarProvider = () => {
  const { client } = useContext(AppContext);

  const handleError = useCallback((err: unknown) => {
    toast.error(extractError(err), defaultToastOptions);
  }, []);

  const [checkInMutate] = useCheckInMutation({
    onError: handleError,
  });

  const [cancelCheckInMutate] = useCancelCheckInMutation({
    onError: handleError,
  });

  const [cancelReservationMutate] = useCancelReservationMutation({
    onError: handleError,
  });

  const [removeFromWaitListMutate] = useRemoveFromWaitListMutation({
    onError: handleError,
  });

  const [removeLateCancelMutate] = useRemoveLateCancelMutation({
    onError: handleError,
  });

  const [bulkCheckinMutate] = useBulkCheckinMutation();

  const checkIn = useCallback(
    async (variables: CheckInMutationVariables) => {
      const { errors } = await checkInMutate({ variables });
      if (errors) {
        throw new Error("Check-in failed");
      } else {
        toast.success("Checked in", defaultToastOptions);
      }
    },
    [checkInMutate],
  );

  const cancelCheckIn = useCallback(
    async (variables: CancelCheckInMutationVariables) => {
      const { errors } = await cancelCheckInMutate({ variables });
      if (errors) {
        throw new Error("Undo failed");
      } else {
        toast.success("Undone", defaultToastOptions);
      }
    },
    [cancelCheckInMutate],
  );

  const cancelReservation = useCallback(
    async (variables: CancelReservationMutationVariables) => {
      const { errors } = await cancelReservationMutate({ variables });
      if (errors) {
        throw new Error("Cancel failed");
      } else {
        toast.success("Reservation canceled", defaultToastOptions);
      }
    },
    [cancelReservationMutate],
  );

  const removeFromWaitList = useCallback(
    async (variables: RemoveFromWaitListMutationVariables) => {
      const { errors } = await removeFromWaitListMutate({ variables });
      if (errors) {
        throw new Error("Error on remove from wait list");
      } else {
        toast.success("Removed from waitlist", defaultToastOptions);
      }
    },
    [removeFromWaitListMutate],
  );

  const removeLateCancel = useCallback(
    async (variables: RemoveLateCancelMutationVariables) => {
      const { errors } = await removeLateCancelMutate({ variables });
      if (errors) {
        throw new Error("Remove failed");
      } else {
        toast.success("Late cancel removed", defaultToastOptions);
      }
    },
    [removeLateCancelMutate],
  );

  const bulkCheckIn = useCallback(
    async (variables: BulkCheckinMutationVariables) => {
      const { data: { bulkCheckin: { failedCheckins } } = {} } =
        await bulkCheckinMutate({ variables });
      const rejected = failedCheckins.length;
      const fulfilled =
        variables.bulkCheckinInput.reservationIds.length - rejected;
      if (fulfilled) {
        toast.success(`${fulfilled} checked in`, defaultToastOptions);
      }
      if (rejected) {
        toast.error(`${rejected} check-ins failed`, defaultToastOptions);
      }
    },
    [bulkCheckinMutate],
  );

  const bulkCancelCheckIn = useCallback(
    async (checkinUuids: string[]) => {
      const promises = checkinUuids.map((uuid) =>
        cancelCheckInMutate({
          variables: {
            cancelCheckinInput: { clientUuid: client.uuid, checkinUuid: uuid },
          },
        }),
      );
      const result = await Promise.allSettled(promises);
      const { fulfilled, rejected } = getTotalByStatus(result);
      if (fulfilled) {
        toast.success(`${fulfilled} undone`, defaultToastOptions);
      }
      if (rejected) {
        toast.error(`${rejected} undos failed`, defaultToastOptions);
      }
    },
    [cancelCheckInMutate],
  );

  const bulkCancelReservation = useCallback(
    async (reservationsUuids: string[]) => {
      const promises = reservationsUuids.map((uuid) =>
        cancelReservationMutate({
          variables: {
            cancelReservationInput: {
              clientUuid: client.uuid,
              reservationId: uuid,
              isLateCancel: undefined,
            },
          },
        }),
      );
      const result = await Promise.allSettled(promises);
      const { fulfilled, rejected } = getTotalByStatus(result);
      if (fulfilled) {
        toast.success(
          `${fulfilled} reservations canceled`,
          defaultToastOptions,
        );
      }
      if (rejected) {
        toast.error(`${rejected} cancels failed`, defaultToastOptions);
      }
    },
    [cancelReservationMutate],
  );

  return {
    checkIn,
    cancelCheckIn,
    cancelReservation,
    removeFromWaitList,
    removeLateCancel,
    bulkCheckIn,
    bulkCancelCheckIn,
    bulkCancelReservation,
  };
};

const getTotalByStatus = (result: PromiseSettledResult<unknown>[]) => {
  const fulfilled = result.filter((item) => item.status === "fulfilled").length;
  const rejected = result.filter((item) => item.status === "rejected").length;
  return {
    fulfilled,
    rejected,
  };
};

const Context = createContext<
  ReturnType<typeof useCalendarProvider> | undefined
>(undefined);

export const CalendarProvider = (props: CalendarProviderProps) => {
  const { children } = props;
  const contextValue = useCalendarProvider();
  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export const useCalendar = () => {
  const contextValue = useContext(Context);
  if (!contextValue) {
    throw new Error("useCalendar must to be used inside an CalendarProvider");
  }
  return contextValue;
};
